import { FC, Suspense, useState } from 'react';
import { AboutTheSkill } from '../../components/AboutTheSkill/AboutTheSkill';
import { ThreeDotLoader } from '../../components/Fallback/ThreeDotsLoader';
import { PracticeProblems } from '../../components/PracticeProblems/PracticeProblems';
import SkillResourceHeader from '../../components/SkillResource/SkillResourceHeader';
import TimeLineWrapper from '../../components/SkillResource/TimeLineWrapper';
import { WorkedExample } from '../../components/WorkedExample/WorkedExample';
import { PreviewModeProvider } from '../../contexts/PreviewModeContext';
import { FALLBACK_SKILL_RESULT } from '../../custom-hooks/useCurrentSkillResult';
import { NO_SKILL_EXIT_SLIP_STATE } from '../../custom-hooks/useSkillExitSlipState';
import { SkillResourceSteps } from '../../models/Timeline';
import { noop } from '../../utils/noop';
import { removeHighlight } from '../../utils/removeSearchHighlight';
import { DemoSkillChallenge } from './DemoSkillChallenge';
import { adaptSkillInfoForAboutTheSkill } from './guidedTour';
import { GuidedTourContent } from './useGuidedTourContent';

interface Props {
  content: GuidedTourContent;
}

export const DemoSkillResource: FC<Props> = ({ content }) => {
  const [currentStep, setCurrentStep] = useState<SkillResourceSteps | null>(SkillResourceSteps.ABOUT_THE_SKILL);
  const [learnosityPPKey, setLearnosityPPKey] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [assessmentInProgress, setAssessmentInProgress] = useState(false);
  const studentStateInfo = adaptSkillInfoForAboutTheSkill(content.skill);
  const skillCode = removeHighlight(content.skill?.code ?? '');

  return (
    <PreviewModeProvider>
      <div id="singleskillAssessment" data-testid="demo-skill-resource" className="xl:tw-grid tw-pt-16">
        {currentStep !== SkillResourceSteps.SKILL_CHALLENGE && (
          <>
            <SkillResourceHeader
              currentStatus={null}
              previousAttempts={[]}
              currentSkillResult={FALLBACK_SKILL_RESULT}
              previousStatus={[]}
              skillName={studentStateInfo[0]?.skill?.name ?? ''}
              allUserActions={null}
              expanded={expanded}
              setExpanded={setExpanded}
              disabledReviewModal
              // The tour runs on canned content with no student behind it, so there are no exit
              // tickets to report on.
              exitSlipState={NO_SKILL_EXIT_SLIP_STATE}
              skillCode={skillCode}
            />
          </>
        )}
        <div className="tw-flex lg:tw-flex-row tw-flex-col tw-gap-4">
          {!assessmentInProgress && (
            <div className="nc_sidebar-timeline tw-w-full lg:tw-flex lg:tw-flex-col tw-py-0 lg:tw-w-4/12 2xl:tw-w-3/12 xl:tw-px-5">
              <TimeLineWrapper
                skillId={content.skill?.id || ''}
                setLearnosityPPKey={setLearnosityPPKey}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                setSkipNewActivityId={noop}
                refetchLrnSession={noop}
                setExpanded={noop}
                setDisabledReviewModal={noop}
                setAssessmentInProgress={setAssessmentInProgress}
              />
            </div>
          )}
          <div
            id="main-content-div"
            className={`tw-w-full tw-flex tw-flex-col ${
              currentStep === SkillResourceSteps.SKILL_CHALLENGE ? '' : 'lg:tw-w-9/12'
            } tw-pt-0 `}
          >
            <Suspense fallback={<ThreeDotLoader dark />}>
              <>
                {currentStep === SkillResourceSteps.ABOUT_THE_SKILL && (
                  <AboutTheSkill
                    studentStateInfo={studentStateInfo}
                    highSupportSkills={content.highSupportSkills}
                    midSupportSkills={content.midSupportSkills}
                  />
                )}
                {currentStep === SkillResourceSteps.WORKED_EXAMPLE && (
                  <WorkedExample
                    videoUrl={content.videoUrl}
                    captionUrlEnglish={content.captionUrlEnglish}
                    captionUrlSpanish={content.captionUrlSpanish}
                  />
                )}
                {currentStep === SkillResourceSteps.PRACTICE_PROBLEMS && content.skill && (
                  <PracticeProblems
                    key={learnosityPPKey}
                    skillCode={skillCode}
                    skillId={content.skill.id}
                    learnosityPPKey={learnosityPPKey}
                  />
                )}
                {currentStep === SkillResourceSteps.SKILL_CHALLENGE && (
                  <DemoSkillChallenge
                    skillCode={skillCode}
                    title={content.skill?.name || ''}
                    assessmentInProgress={assessmentInProgress}
                    setCurrentStep={setCurrentStep}
                    setAssessmentInProgress={setAssessmentInProgress}
                  />
                )}
              </>
            </Suspense>
          </div>
        </div>
      </div>
    </PreviewModeProvider>
  );
};
